import React, { useEffect } from 'react';

/* ─────────────────────────────────────────────────────────────────
   Modal shell for viewing a full concept design.
   variant="browser" — wide desktop-site frame, scrollable content.
   variant="phone"   — narrow phone frame, scrollable content.
───────────────────────────────────────────────────────────────── */
const ConceptModal = ({ open, onClose, variant = 'browser', name, children }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="csm-overlay" onClick={onClose}>
      <button className="csm-close" onClick={onClose} aria-label="Close">✕</button>

      {variant === 'phone' ? (
        <div className="csm-phone" onClick={e => e.stopPropagation()}>
          <div className="csm-phone-notch" />
          <div className="csm-phone-scroll">{children}</div>
        </div>
      ) : (
        <div className="csm-browser" onClick={e => e.stopPropagation()}>
          <div className="csm-browser-bar">
            <span className="csm-browser-dot" style={{ background: '#EA5F5F' }} />
            <span className="csm-browser-dot" style={{ background: '#E8B34C' }} />
            <span className="csm-browser-dot" style={{ background: '#4CAF7D' }} />
            <span className="csm-browser-url">{name}</span>
          </div>
          <div className="csm-browser-scroll">{children}</div>
        </div>
      )}

      <style>{`
        .csm-overlay {
          position: fixed; inset: 0; background: rgba(6,6,10,0.82);
          display: flex; align-items: center; justify-content: center;
          z-index: 9000; padding: 40px 20px; animation: csmFade 0.2s ease;
        }
        @keyframes csmFade { from { opacity: 0; } to { opacity: 1; } }
        .csm-close {
          position: fixed; top: 22px; right: 26px; z-index: 9001;
          width: 40px; height: 40px; border-radius: 50%; border: 1px solid rgba(255,255,255,0.25);
          background: rgba(255,255,255,0.08); color: #fff; font-size: 16px; cursor: pointer;
          display: flex; align-items: center; justify-content: center; transition: background 0.2s;
        }
        .csm-close:hover { background: rgba(255,255,255,0.18); }
        .csm-close:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }

        .csm-browser {
          width: min(920px, 100%); height: min(82vh, 780px); background: #0E1420;
          border-radius: 14px; overflow: hidden; display: flex; flex-direction: column;
          box-shadow: 0 30px 80px rgba(0,0,0,0.5); animation: csmPop 0.25s ease;
        }
        .csm-browser-bar { display: flex; align-items: center; gap: 8px; padding: 12px 16px; background: rgba(255,255,255,0.04); flex-shrink: 0; }
        .csm-browser-dot { width: 10px; height: 10px; border-radius: 50%; }
        .csm-browser-url { margin-left: 10px; font-size: 12px; color: rgba(255,255,255,0.4); font-family: 'Satoshi','DM Sans',sans-serif; }
        .csm-browser-scroll { flex: 1; overflow-y: auto; min-height: 0; }

        .csm-phone {
          width: min(360px, 88vw); height: min(82vh, 740px); background: #151417;
          border-radius: 34px; overflow: hidden; padding: 10px 10px 0; box-sizing: border-box;
          display: flex; flex-direction: column; box-shadow: 0 30px 80px rgba(0,0,0,0.5);
          border: 6px solid #0a0a0c; animation: csmPop 0.25s ease;
        }
        .csm-phone-notch { width: 70px; height: 16px; background: #000; border-radius: 0 0 10px 10px; margin: 0 auto 4px; flex-shrink: 0; }
        .csm-phone-scroll { flex: 1; overflow-y: auto; min-height: 0; border-radius: 20px 20px 0 0; }

        @keyframes csmPop { from { opacity: 0; transform: scale(0.96) translateY(10px); } to { opacity: 1; transform: none; } }

        @media (max-width: 640px) {
          .csm-browser { height: 88vh; border-radius: 10px; }
          .csm-close { top: 12px; right: 12px; }
        }
      `}</style>
    </div>
  );
};

export default ConceptModal;
